/**
 * WhosOnTop — unified leaderboard + copy trading view.
 *
 * Merges the Leaderboard and Copy Trading tabs into a single route.
 * Data: internal DB rankings + Mansa live pricing overlay.
 */
import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { signTone, toneClassName, signedPrefix } from "@/lib/numberColor";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import DashboardGridWrapper from "@/components/dashboard/DashboardGridWrapper";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Trophy, Copy, TrendingUp, Users, Loader2 } from "lucide-react";
import PageHeader from "@/components/dashboard/PageHeader";

export default function WhosOnTop() {
    const { user } = useAuth();
    const [leaders, setLeaders] = useState([]);
    const [copyTargets, setCopyTargets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [copyBusy, setCopyBusy] = useState(null);

    useEffect(() => {
        const load = async () => {
            try {
                const [lb, ct] = await Promise.all([
                    api.get("/leaderboard").then((r) => r.data?.rows || r.data?.rankings || r.data || []).catch(() => []),
                    api.get("/copy-trading/eligible-leaders").then((r) => r.data?.leaders || r.data?.targets || r.data || []).catch(() => []),
                ]);
                setLeaders(Array.isArray(lb) ? lb : []);
                setCopyTargets(Array.isArray(ct) ? ct : []);
            } catch {
                toast.error("Failed to load rankings");
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    const handleCopy = async (targetId) => {
        setCopyBusy(targetId);
        try {
            await api.post("/copy-trading/start", { target_id: targetId });
            toast.success("Now copying this trader");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Copy failed");
        } finally {
            setCopyBusy(null);
        }
    };

    const widgets = useMemo(() => [
        {
            id: "leaderboard", title: "Leaderboard", category: "Rankings",
            layout: { x: 0, y: 0, w: 7, h: 12 }, minW: 5, minH: 6, noPadding: true,
            render: () => (
                <div className="p-4" data-testid="leaderboard-widget">
                    <div className="flex items-center gap-2 mb-4">
                        <Trophy className="w-5 h-5 text-amber-500" />
                        <h2 className="font-bold text-lg">Top Traders</h2>
                    </div>
                    {leaders.length === 0 ? (
                        <div className="text-sm text-muted-foreground py-4">No rankings yet.</div>
                    ) : (
                        <div className="space-y-2 max-h-[400px] overflow-y-auto">
                            {leaders.every((t) => !t.total_trades) && (
                                <div className="text-xs text-muted-foreground pb-2" data-testid="leaderboard-fresh-note">
                                    Rankings are still warming up — everyone shows 0 trades until traders start closing positions. Check back soon.
                                </div>
                            )}
                            {leaders.map((t, i) => (
                                <div key={t.user_id || i} className="flex items-center justify-between p-3 rounded-lg border border-border/50 hover:bg-secondary/40 transition-colors" data-testid={`leader-row-${i}`}>
                                    <div className="flex items-center gap-3">
                                        <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${i < 3 ? "bg-amber-500/20 text-amber-500" : "bg-muted text-muted-foreground"}`}>
                                            {i + 1}
                                        </span>
                                        <div>
                                            <div className="font-semibold text-sm">{t.display_name || t.name || `Trader ${i + 1}`}</div>
                                            <div className="text-xs text-muted-foreground">{t.tier || "Individual"}</div>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className={`font-mono text-sm font-bold ${toneClassName(signTone(t.pnl_pct || 0))}`}>
                                            {signedPrefix(t.pnl_pct || 0)}{Number(t.pnl_pct || 0).toFixed(2)}%
                                        </div>
                                        <div className="text-[10px] text-muted-foreground">
                                            W/R: {Number(t.win_rate || 0).toFixed(0)}% · {t.total_trades || 0} trades
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            ),
        },
        {
            id: "copy-trading", title: "Copy Trading", category: "Copy",
            layout: { x: 7, y: 0, w: 5, h: 12 }, minW: 4, minH: 6, noPadding: true,
            render: () => (
                <div className="p-4" data-testid="copy-trading-widget">
                    <div className="flex items-center gap-2 mb-4">
                        <Copy className="w-5 h-5 text-primary" />
                        <h2 className="font-bold text-lg">Copy a Trader</h2>
                    </div>
                    {copyTargets.length === 0 ? (
                        <div className="text-sm text-muted-foreground py-4">
                            No traders available for copy. Rankings need at least 10 trades.
                        </div>
                    ) : (
                        <div className="space-y-3 max-h-[400px] overflow-y-auto">
                            {copyTargets.map((t, i) => (
                                <div key={t.user_id || i} className="p-3 rounded-lg border border-border/50 space-y-2" data-testid={`copy-target-${i}`}>
                                    <div className="flex items-center justify-between">
                                        <div className="font-semibold text-sm">{t.display_name || `Trader ${i + 1}`}</div>
                                        <Badge variant="outline" className="text-[10px]">
                                            {Number(t.win_rate || 0).toFixed(0)}% W/R
                                        </Badge>
                                    </div>
                                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                        <span className="flex items-center gap-1">
                                            <TrendingUp className="w-3 h-3" />
                                            <span className={toneClassName(signTone(t.pnl_pct || 0))}>{signedPrefix(t.pnl_pct || 0)}{Number(t.pnl_pct || 0).toFixed(2)}%</span>
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <Users className="w-3 h-3" />
                                            {t.copiers || 0} copiers
                                        </span>
                                    </div>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="w-full h-7 text-xs"
                                        onClick={() => handleCopy(t.user_id)}
                                        disabled={copyBusy === t.user_id}
                                        data-testid={`copy-btn-${i}`}
                                    >
                                        {copyBusy === t.user_id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
                                        Start copying
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            ),
        },
    ], [leaders, copyTargets, copyBusy]);

    return (
        <div className="p-6" data-testid="whos-on-top-page">
            <PageHeader
                icon={Trophy}
                eyebrow="Trade"
                title="Who's On Top"
                subtitle="Track top performers and automatically mirror their strategies."
                testId="whos-on-top-header"
            />

            {loading ? (
                <div className="flex items-center gap-2 text-muted-foreground py-8">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading rankings...
                </div>
            ) : (
                <DashboardGridWrapper
                    role="TRADER"
                    viewId="whos-on-top"
                    widgets={widgets}
                    className="mb-3"
                />
            )}
        </div>
    );
}
